/* istanbul ignore file */

import type { FunctionComponent, MutableRefObject } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { GetHandleProps, SliderItem } from 'react-compound-slider';

import { MINIMUM_HANDLE_INDEX } from '../constants/handles';
import { RangeInputHandleWrapper } from '../styles';
import type { AccessibleHandleLabels, RangeInputRanges } from '../types';
import type { RangeInputRefMap } from './RangeInputTextFields';
import { RangeInputTextFieldName } from './RangeInputTextFields';

export interface RangeInputHandlesProps {
  accessibleHandleLabels: AccessibleHandleLabels;
  domain: RangeInputRanges;
  handles: SliderItem[];
  inputTextFieldRefs: MutableRefObject<RangeInputRefMap>;
  valueTextPrefix?: string;
  getHandleProps: GetHandleProps;
}

export const RangeInputHandles: FunctionComponent<RangeInputHandlesProps> = ({
  accessibleHandleLabels: { minimum: minLabel, maximum: maxLabel },
  domain: [min, max],
  getHandleProps,
  handles,
  inputTextFieldRefs,
  valueTextPrefix,
}: RangeInputHandlesProps) => {
  const activeSliderRef = useRef<number | null>(null);
  const [isSliding, setIsSliding] = useState(false);

  useEffect(() => {
    if (activeSliderRef.current != null) {
      const name =
        activeSliderRef.current === 0
          ? RangeInputTextFieldName.Minimum
          : RangeInputTextFieldName.Maximum;

      if (isSliding) {
        inputTextFieldRefs.current[name].forEach((el) => el.focus());
      } else if (!isSliding && activeSliderRef.current != null) {
        inputTextFieldRefs.current[name].forEach((el) => el.blur());
        activeSliderRef.current = null;
      }
    }
  }, [isSliding, inputTextFieldRefs]);

  const handleFocus = (index: number) => {
    activeSliderRef.current = index;
    setIsSliding(true);
  };

  const handleBlur = () => {
    setIsSliding(false);
  };

  return (
    <>
      {handles.map(({ id, value, percent }, index) => {
        const isMinHandle = index === MINIMUM_HANDLE_INDEX;
        const accessibleLabel = isMinHandle ? minLabel : maxLabel;
        const valueText = valueTextPrefix ? `${valueTextPrefix}${value}` : value.toString();

        const { onMouseDown, onTouchStart, onKeyDown } = getHandleProps(id);

        return (
          <RangeInputHandleWrapper
            key={id}
            type="button"
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            aria-valuetext={valueText}
            aria-orientation="horizontal"
            aria-label={accessibleLabel}
            percent={percent}
            data-testid={`range-input-handle__${isMinHandle ? 'min' : 'max'}`}
            data-highlight={activeSliderRef.current === index}
            onTouchStart={(e) => {
              handleFocus(index);
              onTouchStart(e);
            }}
            onTouchEnd={handleBlur}
            onMouseDown={(e) => {
              handleFocus(index);
              onMouseDown(e);
            }}
            onMouseUp={handleBlur}
            onMouseLeave={handleBlur}
            onKeyDown={onKeyDown}
          />
        );
      })}
    </>
  );
};
